import {
  Button,
  Card,
  CardContent,
  Divider,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { batch, useDispatch, useSelector } from "react-redux";
import { useGetAllBrandsQuery, useGetModelsQuery } from "../../app/api/Apis";
import { setBrand, setModel, setYear } from "./CarFilterSlice";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  DatePicker,
  DesktopDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { showLoader } from "../../utils/globalSlice";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CarFilter = () => {
  const dispatch = useDispatch();
  const [cleared, setCleared] = useState(false);

  // get language from store
  const lang = useSelector((state) => state.global.language);

  //get viewport from store
  const isMob = useSelector((state) => state.global.isMobile);

  //get token from store
  const token = useSelector((state) => state?.global?.token);

  //get selected branId from store
  const brandId = useSelector((state) => state?.carFilter?.selectedBrand);

  //get selected model from store
  const selectedModel = useSelector((state) => state?.carFilter?.selectedModel);

  // get selected year from store
  const selectedYear = useSelector((state) => state?.carFilter?.selectedYear);

  // initialize useform hook of react-hook-form
  const {
    control,
    formState: { errors },
    reset,
    resetField,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      brand: "",
      model: "",
      year: null,
    },
  });

  //function to handle change of brand and set in store
  const handleBrandChange = useCallback(
    (event) => {
      dispatch(setBrand(event.target.value));
    },
    [dispatch]
  );

  // function to handle change of model and set it in store
  const handleModelChange = useCallback(
    (event) => {
      dispatch(setModel(event.target.value));
    },
    [dispatch]
  );

  // function to handle change of year and set it in store
  const handleYearChange = useCallback(
    (year) => {
      dispatch(setYear(moment(year).format("YYYY")));
    },
    [dispatch]
  );

  //api query to get all brands
  const {
    isLoading: brandLoading,
    isError: brandError,
    data: brands,
  } = useGetAllBrandsQuery(null, { skip: !token });

  //api query to get all models, refetches when args changed, skipped if token or brand id is not available
  const {
    isLoading: modelLoading,
    isError: modelError,
    data: models,
  } = useGetModelsQuery(brandId, {
    refetchOnMountOrArgChange: true,
    skip: !token || !brandId,
  });

  //function to handle clear filters
  //sets the store values to null
  //resets the input fields
  const handleClearFilter = (type) => {
    switch (type) {
      case "all":
        batch(() => {
          dispatch(setBrand(""));
          dispatch(setModel(""));
          dispatch(setYear(""));
        });
        reset({
          brand: "",
          model: "",
          year: null,
        });
        break;
      case "brand":
        batch(() => {
          dispatch(setBrand(""));
          dispatch(setModel(""));
        });
        resetField("model");
        resetField("brand");
        break;
      case "model":
        dispatch(setModel(""));
        resetField("model");
        break;
      case "year":
        dispatch(setYear(""));
        resetField("year");
        break;
      default:
        break;
    }
  };

  //hook to set loading state in store
  useEffect(() => {
    if (brandLoading || modelLoading) {
      dispatch(showLoader(true));

      return () => {
        dispatch(showLoader(false)); //cancels the effect
      };
    }
  }, [brandLoading, modelLoading]);

  useEffect(() => {
    handleClearFilter("year");
    setTimeout(() => {
      setCleared(false);
    }, 1000);
    return () => {
      clearTimeout();
    };
  }, [cleared]);

  const brandOptions = useMemo(
    () =>
      brands?.map((brand) => (
        <MenuItem key={brand.brandId} value={brand.brandId}>
          {brand.brandNameEn}
        </MenuItem>
      )),
    [brands]
  );

  const modelOptions = useMemo(
    () =>
      models?.carModelList?.map((model) => (
        <MenuItem value={model?.modelId} key={model?.modelId}>
          {model?.modelNameEn}
        </MenuItem>
      )),
    [models]
  );

  return (
    <Card sx={{ mb: 3, borderRadius: "1rem" }} elevation={2}>
      <CardContent
        sx={{
          "& .MuiFormControl-root": {
            "& label": {
              transformOrigin: lang === "ar" ? "right" : "left",
              left: lang === "ar" ? "inherit" : 0,
              right: lang === "ar" ? "1.75rem" : "0",
            },
            "& legend": {
              textAlign: lang === "ar" ? "right" : "left",
            },
            "& .MuiFormHelperText-root": {
              textAlign: lang === "ar" ? "right" : "left",
            },
            "& svg": {
              right: lang === "ar" ? "92%" : "1rem",
            },
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ mb: 2 }}>
            <FormattedMessage id="app.filter.title" />:
          </Typography>
          <Fade
            in={brandId !== "" || selectedModel !== "" || selectedYear !== ""}
            sx={{ mb: 2 }}
          >
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => handleClearFilter("all")}
            >
              Clear all
            </Button>
          </Fade>
        </Stack>

        <form>
          <Stack direction={!!isMob ? "column" : "row"} spacing={1} useFlexGap>
            <FormControl fullWidth>
              <Controller
                name="brand"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    label={<FormattedMessage id="app.filter.brand" />}
                    size="small"
                    select={brands ? true : false}
                    className="borderless"
                    onChange={(e) => {
                      field.onChange(e);
                      handleBrandChange(e);
                    }}
                    // InputProps={{
                    //   endAdornment: (
                    //     <InputAdornment
                    //       position="end"
                    //       sx={{
                    //         marginRight: lang === "en" && "24px",
                    //         marginLeft: lang === "ar" && "30px",
                    //       }}
                    //     >
                    //       {brandId && (
                    //         <IconButton
                    //           onClick={() => handleClearFilter("brand")}
                    //           size="small"
                    //         >
                    //           <ClearIcon sx={{fontSize: '1.25rem'}} />
                    //         </IconButton>
                    //       )}
                    //     </InputAdornment>
                    //   ),
                    // }}
                    SelectProps={{
                      IconComponent: brandId
                        ? (props) => (
                            <IconButton
                              onClick={() => handleClearFilter("brand")}
                              size="small"
                            >
                              <ClearIcon sx={{ fontSize: "1.25rem" }} />
                            </IconButton>
                          )
                        : ExpandMoreIcon, // Set ExpandMoreIcon directly when no brandId
                    }}
                  >
                    {/* {brands?.map((brand) => (
                      <MenuItem key={brand.brandId} value={brand.brandId}>
                        {brand.brandNameEn}
                      </MenuItem>
                    ))} */}
                    {brandOptions}
                  </TextField>
                )}
              />
            </FormControl>
            <Divider
              orientation={isMob ? "horizontal" : "vertical"}
              sx={{ borderColor: "white.dark", height: "auto" }}
            />
            <FormControl fullWidth>
              <Controller
                name="model"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    label={<FormattedMessage id="app.filter.model" />}
                    size="small"
                    select={models ? true : false}
                    className="borderless"
                    onChange={(e) => {
                      field.onChange(e);
                      handleModelChange(e);
                    }}
                    disabled={!brandId}
                    // InputProps={{
                    //   endAdornment: (
                    //     <InputAdornment
                    //       position="end"
                    //       sx={{
                    //         marginRight: lang === "en" && "24px",
                    //         marginLeft: lang === "ar" && "30px",
                    //       }}
                    //     >
                    //       {selectedModel && (
                    //         <IconButton
                    //           onClick={() => handleClearFilter("model")}
                    //           size="small"
                    //         >
                    //           <ClearIcon sx={{ fontSize: "1.25rem" }} />
                    //         </IconButton>
                    //       )}
                    //     </InputAdornment>
                    //   ),
                    // }}
                    SelectProps={{
                      IconComponent: selectedModel
                        ? (props) => (
                            <IconButton
                              onClick={() => handleClearFilter("model")}
                              size="small"
                            >
                              <ClearIcon sx={{ fontSize: "1.25rem" }} />
                            </IconButton>
                          )
                        : ExpandMoreIcon, // Set ExpandMoreIcon directly when no brandId
                    }}
                  >
                    {models?.carModelList?.length === 0 ? (
                      <MenuItem disabled>
                        <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                        <Typography variant="caption">
                          No models found
                        </Typography>
                      </MenuItem>
                    ) : (
                      modelOptions
                    )}
                  </TextField>
                )}
              />
            </FormControl>
            <Divider
              orientation={isMob ? "horizontal" : "vertical"}
              sx={{ borderColor: "white.dark", height: "auto" }}
            />
            <FormControl fullWidth>
              <Controller
                name="year"
                control={control}
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DesktopDatePicker
                      {...field}
                      value={field.value}
                      format="YYYY"
                      label={<FormattedMessage id="app.filter.year" />}
                      disableFuture
                      slotProps={{
                        textField: {
                          size: "small",
                          error: !!errors.year,
                        },
                        field: {
                          clearable: true,
                          onClear: () => setCleared(true),
                        },
                      }}
                      views={["year"]}
                      onChange={(year) => {
                        field.onChange(year);
                        handleYearChange(year);
                      }}
                    />
                  </LocalizationProvider>
                )}
              />
            </FormControl>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
};

export default CarFilter;
